import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { useTheme } from '@mui/material/styles'
import Grid from '@mui/material/Grid2'
import ErrorOverlay from 'Components/General/ErrorOverlay'
import AllocationScopePicker from 'Components/reusable/InputFields/AllocationScopePicker'
import RegularCheckbox from 'Components/reusable/InputFields/RegularCheckbox'
import { useUser } from 'hooks'
import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { IAllocationManagementTableData, IAllocationScopeItem } from 'types/allocationInterfaces'
import { IAllocation } from 'types/assignmentInterfaces'
import { IError } from 'types/error'
import PeopleAllocationTable from './PeopleAllocationTable'
import { personAPI } from 'api/PersonAPI'
import { IPerson } from 'types/userInterfaces'
import OverviewSummary from './OverviewSummary/OverviewSummary'
import { useLocalStorage } from 'hooks/data'

/** Keys of the locally-stored allocation overview filters. */
type AllocationOverviewFilterKey =
  | 'other'
  | 'assignment'
  | 'absence'
  | 'free'
  | 'partial'
  | 'full'
  | 'verified'
  | 'tentative'

/** @notExported  */
interface IAllocationOverviewProps {
  /** Function to set the edited item  */
  setEditedItem: (value: IAllocation | null | undefined) => void
  /** Function to delete an item */
  deleteItem: (value: IAllocation) => Promise<void>
  /** Function to get the options for the scope picker*/
  getOptions: () => IAllocationScopeItem[]
  /** Sales access permissions */
  salesAccess: boolean
  /** Update from editing  */
  updateFromEditing: Date | undefined
}

/**
 * Allocation overview component.
 *
 * @returns Allocation overview component.
 * @notExported
 */
const AllocationOverview: React.FC<IAllocationOverviewProps> = ({
  setEditedItem,
  deleteItem,
  getOptions,
  salesAccess,
  updateFromEditing,
}) => {
  const theme = useTheme()
  const { user } = useUser()
  const { t } = useTranslation()

  const [backendError, setBackendError] = useState<IError>()
  const [showOutdated, setShowOutdated] = useState<boolean>(false)
  const [showActive, setShowActive] = useState<boolean>(true)
  const [selectedScope, setSelectedScope] = useState<{ value: number; label: string; type: string }>()
  const [managementTableData, setManagementTableData] = useState<IAllocationManagementTableData[]>([])
  const [overviewUpdate, setOverviewUpdate] = useState<boolean>(false)
  const [tab, setTab] = useState<number>(0)
  const [personIds, setPersonIds] = useState<number[]>([])
  const [timeScope, setTimeScope] = useState<number>(1)
  const [filters, setFilters] = useLocalStorage('filters', {
    other: true,
    assignment: true,
    absence: true,
    free: true,
    partial: true,
    full: true,
    verified: true,
    tentative: true,
  })

  useEffect(() => {
    const controller = new AbortController()

    ;(async () => {
      if (overviewUpdate || updateFromEditing) {
        try {
          const personIds: number[] = []
          let peopleWithAllocations: IPerson[] = []

          if (selectedScope?.type === 'own-company' && user) {
            peopleWithAllocations = await personAPI.getCompanyMembersAllocations(
              user.organizationId,
              timeScope,
              controller
            )
          }

          if (selectedScope?.type === 'team' && selectedScope?.value) {
            peopleWithAllocations = await personAPI.getTeamMembersAllocations(
              selectedScope.value,
              timeScope,
              controller
            )
          } else if (selectedScope?.type === 'company' && selectedScope?.value) {
            peopleWithAllocations = await personAPI.getCompanyMembersAllocations(
              selectedScope.value,
              timeScope,
              controller
            )
          }
          if (peopleWithAllocations.length) {
            const arrayOfAllocations: IAllocationManagementTableData[] = []

            for (const person of peopleWithAllocations) {
              if (person?.Allocations?.length) {
                for (const alloc of person.Allocations) {
                  if (alloc.startDate) alloc.startDate = new Date(alloc.startDate)
                  if (alloc.endDate) alloc.endDate = new Date(alloc.endDate)

                  arrayOfAllocations.push({
                    firstName: person.firstName,
                    lastName: person.lastName,
                    id: person.id,
                    Allocation: alloc,
                  })
                }
              } else {
                arrayOfAllocations.push({ firstName: person.firstName, lastName: person.lastName, id: person.id })
              }

              personIds.push(person.id)
            }
            setPersonIds(personIds)
            setManagementTableData(arrayOfAllocations)
          } else {
            setManagementTableData([])
          }
        } catch (error) {
          setBackendError(error as IError)
        } finally {
          {
            setOverviewUpdate(false)
          }
        }
      }
    })()

    return () => {
      controller.abort()
    }
  }, [overviewUpdate, selectedScope?.type, selectedScope?.value, updateFromEditing, user, timeScope])

  const tableData: IAllocationManagementTableData[] = useMemo(() => {
    if (!showOutdated && showActive) {
      return managementTableData?.filter(a => !a?.Allocation?.endDate || a?.Allocation?.endDate >= new Date())
    } else if (showOutdated && showActive) {
      return managementTableData
    } else if (!showActive && showOutdated) {
      return managementTableData?.filter(a => !a?.Allocation?.endDate || a?.Allocation?.endDate <= new Date())
    }
    return []
  }, [managementTableData, showOutdated, showActive])

  const changeTab = (_event: unknown, newValue: number) => {
    setTab(newValue)
  }

  const changeFilter = (filter: AllocationOverviewFilterKey) => {
    const newFilters: Record<AllocationOverviewFilterKey, boolean> = { ...filters }

    newFilters[filter] = !newFilters[filter]
    setFilters(newFilters)
  }

  const filterControls = () => {
    return (
      <Grid size={12}>
        <Grid container alignItems="center" spacing={1} size="auto">
          <Grid
            sx={{ borderRight: '1px solid #000', marginRight: 2 }}
            container
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <Grid size={12}>
              <Typography fontWeight="bold">{t('allocation.state')}</Typography>
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.tentative`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-tentative"
                    checked={filters['tentative']}
                    onChange={() => changeFilter('tentative')}
                  />
                }
              />
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.verified`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-verified"
                    checked={filters['verified']}
                    onChange={() => changeFilter('verified')}
                  />
                }
              />
            </Grid>
          </Grid>
          <Grid
            sx={{ borderRight: '1px solid #000', marginRight: 2 }}
            container
            size={{
              xs: 12,
              lg: 4,
            }}
          >
            <Grid size={12}>
              <Typography fontWeight="bold">{t('allocation.type')}</Typography>
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.otherWork`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-other"
                    checked={filters['other']}
                    onChange={() => changeFilter('other')}
                  />
                }
              />
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.assignment`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-assignment"
                    checked={filters['assignment']}
                    onChange={() => changeFilter('assignment')}
                  />
                }
              />
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.absence`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-absence"
                    checked={filters['absence']}
                    onChange={() => changeFilter('absence')}
                  />
                }
              />
            </Grid>
          </Grid>
          <Grid
            sx={{ borderRight: '1px solid #000', marginRight: 2 }}
            container
            size={{
              xs: 12,
              lg: 3,
            }}
          >
            <Grid size={12}>
              <Typography fontWeight="bold">{t('allocation.percentage')}</Typography>
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.free`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-free"
                    checked={filters['free']}
                    onChange={() => changeFilter('free')}
                  />
                }
              />
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.partial`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-partial"
                    checked={filters['partial']}
                    onChange={() => changeFilter('partial')}
                  />
                }
              />
            </Grid>
            <Grid size="auto">
              <FormControlLabel
                label={t(`allocation.full`)}
                control={
                  <Checkbox
                    data-testid="checkbox-allocation-overview-filter-full"
                    checked={filters['full']}
                    onChange={() => changeFilter('full')}
                  />
                }
              />
            </Grid>
          </Grid>
          <Grid
            container
            size={{
              xs: 12,
              lg: 1,
            }}
          >
            <Grid size={12}>
              <Typography fontWeight="bold">{t('allocation.overview.timeScope')}</Typography>
            </Grid>
            <Grid size={12}>
              <Select
                data-testid="select-allocation-overview-time-scope"
                onChange={event => {
                  if (event) setTimeScope(event.target.value as number)
                }}
                value={timeScope}
                size="small"
                fullWidth
              >
                <MenuItem value={1}>{t('oneMonth')}</MenuItem>
                <MenuItem value={2}>{t('twoMonths')}</MenuItem>
                <MenuItem value={3}>{t('threeMonths')}</MenuItem>
                <MenuItem value={4}>{t('fourMonths')}</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const legend = () => {
    return (
      <Grid
        size={{
          xs: 12,
          md: 6,
          lg: 4,
        }}
      >
        <Grid container alignItems="end">
          <Grid container wrap="nowrap" alignItems="center" size={3} sx={{ whiteSpace: 'nowrap' }}>
            <Grid
              size={12}
              sx={{
                width: '15px',
                height: '15px',
                borderRadius: '30%',
                opacity: 0.5,
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.primary.main}`,
                marginRight: 0.5,
              }}
            />
            <span>0%</span>
          </Grid>
          <Grid container wrap="nowrap" alignItems="center" size={3} sx={{ whiteSpace: 'nowrap' }}>
            <Grid
              size={12}
              sx={{
                width: '15px',
                height: '15px',
                borderRadius: '30%',
                backgroundColor: theme.palette.warning.main,
                marginRight: 0.5,
              }}
            />
            <span>1-99%</span>
          </Grid>
          <Grid container wrap="nowrap" alignItems="center" size={3} sx={{ whiteSpace: 'nowrap' }}>
            <Grid
              size={12}
              sx={{
                width: '15px',
                height: '15px',
                borderRadius: '30%',
                backgroundColor: theme.palette.error.main,
                marginRight: 0.5,
              }}
            />
            <span>100%</span>
          </Grid>
          <Grid container wrap="nowrap" alignItems="center" size={3} sx={{ whiteSpace: 'nowrap' }}>
            <Grid
              size={12}
              sx={{
                width: '15px',
                height: '15px',
                borderRadius: '30%',
                opacity: 0.8,
                backgroundColor: theme.palette.info.main,
                marginRight: 0.5,
              }}
            />
            <span>100+%</span>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  if (backendError && backendError.name !== 'CanceledError' && backendError.name !== 'AbortError') {
    return <ErrorOverlay error={backendError} setOpen={setBackendError} />
  }

  return (
    <>
      <Grid container spacing={2} direction="row">
        <Grid size={12}>
          <Typography variant="h6" fontWeight="bold" mt={2}>
            {t('allocation.filter')}
          </Typography>
        </Grid>
        <Grid size={4}>
          <AllocationScopePicker
            value={selectedScope}
            onChange={scope => {
              setSelectedScope(scope)
              setOverviewUpdate(true)
            }}
            items={getOptions() || []}
            label={t('allocation.scope')}
            insetLabel
          />
        </Grid>
        {tab === 1 && (
          <>
            <Grid>
              <RegularCheckbox
                checked={showActive}
                onChange={() => setShowActive(!showActive)}
                label={t(`allocation.showActive`)}
              />
            </Grid>
            <Grid>
              <RegularCheckbox
                checked={showOutdated}
                onChange={() => setShowOutdated(!showOutdated)}
                label={t(`allocation.showOutdated`)}
              />
            </Grid>
          </>
        )}
        {tab === 0 && filterControls()}
      </Grid>
      <Grid size={12}>
        <Typography variant="h6" fontWeight="bold" mt={2}>
          {t('allocation.results')}
        </Typography>
      </Grid>
      <Grid container justifyContent="space-between" alignItems="flex-end" size={12}>
        <Grid
          size={{
            xs: 12,
            md: 6,
            lg: 8,
          }}
        >
          <Tabs value={tab} onChange={changeTab} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile>
            <Tab label={t('allocation.overview.summary')} />
            <Tab label={t('allocation.overview.edit')} />
          </Tabs>
        </Grid>
        {tab === 0 && legend()}
      </Grid>
      {tab === 0 && (
        <OverviewSummary
          filters={filters}
          timeScope={timeScope}
          scope={personIds}
          deleteItem={deleteItem}
          setEditedItem={setEditedItem}
          updateFromEditing={updateFromEditing}
        />
      )}
      {tab === 1 && (
        <>
          {user?.Person?.id && (
            <PeopleAllocationTable
              items={tableData}
              openItem={(item: IAllocation) => {
                setEditedItem(item)
              }}
              deleteItem={deleteItem}
              personId={user.Person.id}
              salesAccess={salesAccess}
            />
          )}
        </>
      )}
    </>
  )
}

export default AllocationOverview
